import Shepherd from 'shepherd.js';
import {
  autoSelectByLabel,
  autoSelectInContainer,
  expandAllTreeNodes,
  findLeafElement,
  injectStepUI,
  markTourStartDate,
  selectLeafComponent,
  setButtonLoading,
  waitForElement,
  waitForEnabledSelect
} from './TourHelpers';
import type { ShepherdTour, TrackableTour } from './TourHelpers';

export function createOEMSearchTour(): ShepherdTour {
  const tour = new Shepherd.Tour({
    useModalOverlay: true,
    defaultStepOptions: {
      classes: 'shepherd-theme-custom',
      scrollTo: true,
      cancelIcon: {
        enabled: true
      },
      modalOverlayOpeningPadding: 6,
      modalOverlayOpeningRadius: 6
    }
  });
  (tour as TrackableTour).__tourStartDate = null;

  tour.addStep({
    id: "step1",
    text: '<div class="tour-step-counter">1 of 7</div><strong class="tour-step-header">Did you know? We have an enhanced <em>Parts Lookup</em> feature.</strong><br>This allows you to search for both OEM and competitive truck models and identify compatible parts.',
    attachTo: {
      element: '#tmsButtonGroup a[href*="/tms/partsLookup"]',
      on: "bottom"
    },
    canClickTarget: false,
    scrollTo: false,
    when: {
      show() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        injectStepUI(this.getElement(), 1, 6);
      }
    },
    buttons: [
      {
        text: "Next",
        action: async () => {
          markTourStartDate(tour);
          setButtonLoading(tour, true);
          if (!document.querySelector('#psts__make')) {
            const link = document.querySelector('#tmsButtonGroup a[href*="/tms/partsLookup"]') as HTMLElement;
            if (link) {
              link.click();
            }
          }
          await waitForElement('#psts__make', 15000);
          tour.next();
        }
      }
    ]
  });

  tour.addStep({
    id: "step2",
    text: '<div class="tour-step-counter">2 of 6</div><strong class="tour-step-header">Select the truck Make</strong><br>Type or choose the manufacturer to locate the correct OEM truck.',
    attachTo: {
      element: '#psts__make',
      on: "right"
    },
    canClickTarget: false,
    when: {
      show() {
        injectStepUI(this.getElement(), 2, 6);
      }
    },
    buttons: [
      {
        text: "Next",
        action: async () => {
          setButtonLoading(tour, true);
          await autoSelectInContainer('#psts__make', 'OemDescription', 'CAT LIFT TRUCKS');
          await waitForEnabledSelect('label[for="modelSelect"]', 15000);
          tour.next();
        }
      }
    ]
  });

  tour.addStep({
    id: "step3",
    text: '<div class="tour-step-counter">3 of 6</div><strong class="tour-step-header">Select the Model</strong><br>Enter or pick your specific model.',
    attachTo: {
      element: '#psts__model',
      on: "right"
    },
    canClickTarget: false,
    when: {
      show() {
        injectStepUI(this.getElement(), 3, 6);
      }
    },
    buttons: [
      {
        text: "Next",
        action: async () => {
          setButtonLoading(tour, true);
          await autoSelectByLabel('modelSelect', 'ModelName', 'EC25N');
          await waitForEnabledSelect('label[for="componentGroupSelect"]', 15000);
          tour.next();
        }
      }
    ]
  });

  tour.addStep({
    id: "step4",
    text: '<div class="tour-step-counter">4 of 6</div><strong class="tour-step-header">Choose a Publication</strong><br>Select the relevant publication to access detailed parts information.',
    attachTo: {
      element: '#psts__publication',
      on: "right"
    },
    canClickTarget: false,
    when: {
      show() {
        injectStepUI(this.getElement(), 4, 6);
      }
    },
    buttons: [
      {
        text: "Next",
        action: async () => {
          setButtonLoading(tour, true);
          await autoSelectByLabel('componentGroupSelect', 'PublicationModuleTitle', 'CHASSIS, MAST & OPTIONS');
          await waitForElement('.tree-node__label', 20000);
          tour.next();
        }
      }
    ]
  });

  tour.addStep({
    id: "step5",
    text: '<div class="tour-step-counter">5 of 6</div><strong class="tour-step-header">Navigate to the Component</strong><br>Browse through the component hierarchy until you find the section you need.',
    attachTo: {
      element: '.tree-node--root',
      on: "right"
    },
    canClickTarget: false,
    when: {
      show() {
        injectStepUI(this.getElement(), 5, 6);
      }
    },
    buttons: [
      {
        text: "Next",
        action: async () => {
          setButtonLoading(tour, true);
          expandAllTreeNodes();
          await waitForElement('.tree-node__label', 20000);
          tour.next();
        }
      }
    ]
  });

  tour.addStep({
    id: "step6",
    text: '<div class="tour-step-counter">6 of 6</div><strong class="tour-step-header">Select the Component</strong><br>Click on the component to load its associated parts list.',
    attachTo: {
      element: () => findLeafElement('01-71-01 HYDRAULIC PUMPS'),
      on: "right"
    },
    canClickTarget: false,
    scrollTo: { behavior: 'smooth', block: 'center' },
    when: {
      show() {
        injectStepUI(this.getElement(), 6, 6);
      }
    },
    buttons: [
      {
        text: "Close",
        action: async () => {
          selectLeafComponent('01-71-01 HYDRAULIC PUMPS');
          tour.complete();
        }
      }
    ]
  });

  return tour;
}